import React from "react";

/**
 * ToolSplitLayout — the field HMI "two-panel" shell used by every tool
 * page (Ovality, Verticality, Diagonal…).
 *
 *   ┌───────────────────────────────┬─────────────────┐
 *   │  DATA (scrollable)            │ CONTROLS (fixed)│
 *   │  ─ Session header             │ ─ Shot button   │
 *   │  ─ Stats + visualizations     │ ─ Tolerance     │
 *   │  ─ Shot tables                │ ─ Target chip   │
 *   │  ─ Photo overlay              │ ─ Session nav   │
 *   │  ▼ scrolls                    │                 │
 *   └───────────────────────────────┴─────────────────┘
 *
 * On tablets in portrait we stack: controls sit UNDER the data column
 * so the surveyor's dominant thumb still reaches the shot button. This
 * automatic reflow uses tailwind's `landscape:` / `portrait:` media
 * variants — no JS.
 *
 * Props:
 *   left        — the data column (scrolls independently)
 *   right       — the controls column (fixed, never scrolls)
 *   rightWidth  — override the landscape width of the controls rail
 *                 (default 360px — comfortable for one-thumb use)
 */
export function ToolSplitLayout({ left, right, rightWidth = "360px" }) {
  // When there's no controls column to show (e.g. Diagonal results
  // view where only the plan + stats matter) we collapse the layout
  // to a single scrollable column. Passing an explicit `null` — not
  // "no right prop" — is enough: `right == null` covers both.
  const hasRight = right != null;
  return (
    <div
      className={`grid h-[calc(100dvh-56px)] w-full overflow-hidden bg-[#0A0B0E] ${hasRight ? "portrait:grid-rows-[1fr_auto]" : ""}`}
      style={{ gridTemplateColumns: undefined }}
      data-testid="tool-split-layout"
    >
      <style>{`
        @media (orientation: landscape) {
          [data-testid="tool-split-layout"] {
            grid-template-columns: ${hasRight ? `1fr ${rightWidth}` : "1fr"} !important;
            grid-template-rows: none !important;
          }
        }
      `}</style>

      {/* Left — data column. Scrollable, tablet-friendly padding. */}
      <div
        className="min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-5"
        data-testid="tool-split-left"
      >
        {left}
      </div>

      {hasRight && (
        <aside
          className="flex min-h-0 flex-col gap-3 overflow-y-auto border-t border-[#2A2E39] bg-[#0F1116] p-3 landscape:border-l landscape:border-t-0 landscape:p-4 no-print"
          data-testid="tool-split-right"
        >
          {right}
        </aside>
      )}
    </div>
  );
}

export default ToolSplitLayout;
